"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { format, parse } from "date-fns";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Calendar } from "@/components/ui/calendar";
import { getDateFnsLocale, getDateStringLocale } from "@/i18n/getDateFnsLocale";
import { getCurrencySymbol, PriceCurrency } from "@/features/calendar/types/pricing.tsx";

type EditPaymentDialogProps = {
  open: boolean;
  paymentId: string | null;
  current?: {
    amount?: string | null; // formatted like "$45.00"
    currency?: string | null; // inferred from amount formatting; pass if known
    payment_due?: string | null; // YYYY-MM-DD
    description?: string | null;
    isCompleted?: boolean;
  };
  onOpenChange: (open: boolean) => void;
  onUpdated?: () => Promise<void> | void;
};

const useEditSchema = (t: any) =>
  z
    .object({
      amount: z
        .string()
        .trim()
        .optional()
        .refine(
          (v) => !v || /^\d+(?:[.,]\d{1,2})?$/.test(v),
          t("finances:paymentsEdit.validation.amountNumeric", "Amount must be a number"),
        )
        .refine(
          (v) => !v || parseFloat((v || "0").replace(",", ".")) >= 0.01,
          t("finances:paymentsEdit.validation.amountMin", "Amount must be at least 0.01"),
        ),
      currency: z
        .string()
        .optional()
        .refine(
          (v) => !v || ["USD", "EUR", "GBP", "PLN"].includes(v),
          t("finances:paymentsEdit.validation.currencyInvalid", "The selected currency is invalid"),
        ),
      payment_due: z.string().trim().optional(),

      description: z
        .string()
        .trim()
        .max(
          2000,
          t(
            "finances:paymentsEdit.validation.descriptionMax",
            "Description may not be greater than 2000 characters.",
          ),
        )
        .optional(),
    })
    .refine((data) => Object.values(data).some((v) => (v ?? "").toString().trim() !== ""), {
      message: t(
        "finances:paymentsEdit.validation.atLeastOne",
        "Provide at least one field to update",
      ),
      path: ["amount"],
    });

export default function EditPaymentDialog({
  open,
  paymentId,
  current,
  onOpenChange,
  onUpdated,
}: EditPaymentDialogProps) {
  const { t, i18n } = useTranslation(["finances", "common"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [descLength, setDescLength] = useState(0);

  const schema = useMemo(() => useEditSchema(t), [t]);

  type FormData = z.infer<typeof schema>;
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: current?.amount ? current.amount.replace(/[^0-9.,]/g, "") : "",
      currency: current?.currency || undefined,
      payment_due: current?.payment_due || "",
      description: current?.description || "",
    },
  });

  const currency = form.watch("currency") as PriceCurrency | undefined;

  // compact layout – no presets or quick actions

  useEffect(() => {
    if (open) {
      form.reset({
        amount: current?.amount ? current.amount.replace(/[^0-9.,]/g, "") : "",
        currency: current?.currency || undefined,
        payment_due: current?.payment_due || "",
        description: current?.description || "",
      });
      setDescLength(current?.description ? current.description.length : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current?.amount, current?.currency, current?.payment_due, current?.description]);

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : handleClose())}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-background/75 text-accent-foreground max-h-[85dvh] max-w-3xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl md:max-w-3xl"
      >
        <ScrollArea className="relative max-h-[85dvh]">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-4">
              <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
                <div className="bg-muted/20 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                  <Icon
                    className="text-accent-foreground"
                    height="1.25rem"
                    icon="mdi:pencil"
                    width="1.25rem"
                  />
                </div>
                {t("finances:paymentsEdit.title", "Edit payment")}
              </DialogTitle>
            </DialogHeader>

            {current?.isCompleted && (
              <Card className="mb-4 border-amber-500/30 bg-amber-500/10 p-3">
                <div className="text-sm text-amber-600">
                  {t("finances:paymentsEdit.completedHint", "Completed payments cannot be edited.")}
                </div>
              </Card>
            )}

            <Form {...form}>
              <form
                className="space-y-6"
                onSubmit={form.handleSubmit(async (values) => {
                  if (!paymentId) return;
                  try {
                    setIsSubmitting(true);

                    const payload: Record<string, any> = {};

                    if (values.amount && values.amount.trim()) {
                      payload.amount = parseFloat(values.amount.replace(",", "."));
                    }
                    if (values.currency) payload.currency = values.currency;
                    if (values.payment_due && values.payment_due.trim()) {
                      payload.payment_due = format(values.payment_due, "yyyy-MM-dd").trim();
                    }
                    if (values.description && values.description.trim())
                      payload.description = values.description.trim();

                    await axiosApiInstance.patch(
                      API_ENDPOINTS.TUTOR.FINANCES.PAYMENT_UPDATE(paymentId),
                      Object.keys(payload).length ? payload : undefined,
                    );

                    toast.success(t("finances:paymentsEdit.updated", "Payment updated"));
                    onOpenChange(false);
                    if (onUpdated) await onUpdated();
                  } catch (err: any) {
                    if (err?.response?.status === 422 && err?.response?.data?.errors) {
                      const ve = err.response.data.errors;

                      if (ve.amount?.[0])
                        form.setError("amount", { type: "server", message: ve.amount[0] });
                      if (ve.currency?.[0])
                        form.setError("currency", { type: "server", message: ve.currency[0] });
                      if (ve.payment_due?.[0])
                        form.setError("payment_due", {
                          type: "server",
                          message: ve.payment_due[0],
                        });
                      if (ve.description?.[0])
                        form.setError("description", {
                          type: "server",
                          message: ve.description[0],
                        });
                    } else if (err?.response?.data?.error === "cannot_edit_completed") {
                      toast.error(
                        t(
                          "finances:paymentsEdit.errors.cannotEditCompleted",
                          "Completed payments cannot be edited.",
                        ),
                      );
                    } else {
                      toast.error(
                        err?.response?.data?.message ||
                          t("finances:paymentsEdit.errors.generic", "Failed to update payment"),
                      );
                    }
                  } finally {
                    setIsSubmitting(false);
                  }
                })}
              >
                {/* Amount + currency (pill select + right-aligned amount) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-accent-foreground flex items-center gap-2 text-sm font-medium">
                      <Icon height="1rem" icon="mdi:cash" width="1rem" />
                      {t("finances:paymentsEdit.amount", "Amount")}
                    </label>
                    <FormField
                      control={form.control}
                      name="currency"
                      render={({ field }) => (
                        <FormItem>
                          <div className="bg-muted hover:bg-side-accent flex h-8 items-center rounded-full px-3">
                            <Select
                              value={field.value as any}
                              onValueChange={(v) => field.onChange(v as PriceCurrency)}
                            >
                              <FormControl>
                                <SelectTrigger className="h-8 min-w-[72px] border-0 bg-transparent p-0 px-1 text-xs shadow-none focus:ring-0 focus:ring-offset-0">
                                  <SelectValue
                                    placeholder={t(
                                      "finances:paymentsEdit.currencyPlaceholder",
                                      "Choose currency",
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent align="center" className="z-50">
                                {(
                                  [
                                    PriceCurrency.Eur,
                                    PriceCurrency.Gbp,
                                    PriceCurrency.Pln,
                                    PriceCurrency.Usd,
                                  ] as const
                                ).map((c) => (
                                  <SelectItem key={c} value={c}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs">{getCurrencySymbol(c)}</span>
                                      <span className="text-xs sm:text-sm">{c}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <div className="bg-muted/20 group relative rounded-md backdrop-blur-sm">
                          <div className="absolute top-1/2 left-0 flex h-12 w-12 -translate-y-1/2 items-center justify-center sm:h-12 sm:w-16">
                            <span className="text-accent-foreground text-lg font-semibold sm:text-xl">
                              {getCurrencySymbol(currency ?? PriceCurrency.Usd)}
                            </span>
                          </div>
                          <FormControl>
                            <Input
                              className={`hover:bg-muted/30 focus:bg-muted/20 h-12 appearance-none border-0 bg-transparent pr-3 pl-14 text-base font-semibold transition-colors sm:pl-16 lg:text-lg ${
                                form.formState.errors.amount ? "ring-1 ring-red-500" : ""
                              }`}
                              placeholder={t(
                                "finances:paymentsEdit.amountPlaceholder",
                                "e.g. 45.00",
                              )}
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <FormMessage>
                          {form.formState.errors.amount && (
                            <div className="text-destructive flex items-center gap-1">
                              <Icon height="1rem" icon="mdi:alert-circle-outline" width="1rem" />
                              <span>{form.formState.errors.amount.message}</span>
                            </div>
                          )}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Payment due with calendar popover */}
                <FormField
                  control={form.control}
                  name="payment_due"
                  render={({ field }) => {
                    console.log(field.value);
                    const selectedDate = field.value
                      ? parse(field.value, "yyyy-MM-dd HH:mm", new Date())
                      : null;

                    console.log(selectedDate);

                    return (
                      <FormItem>
                        <FormLabel className="text-accent-foreground flex items-center gap-2">
                          <Icon height="1rem" icon="mdi:calendar" width="1rem" />
                          {t("finances:paymentsEdit.paymentDue", "Payment due")}
                        </FormLabel>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className="bg-muted/20 hover:bg-muted/30 group flex h-12 w-full items-center justify-between rounded-xl border-none px-4 text-left backdrop-blur-sm transition-colors"
                                variant="outline"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="bg-muted/30 flex size-8 items-center justify-center rounded-lg backdrop-blur-sm">
                                    <Icon
                                      className="text-accent-foreground"
                                      height="1.1rem"
                                      icon="tabler:calendar"
                                      width="1.1rem"
                                    />
                                  </div>
                                  <span className="text-accent-foreground font-medium">
                                    {selectedDate
                                      ? selectedDate.toLocaleDateString(
                                          getDateStringLocale(i18n.language),
                                        )
                                      : t(
                                          "finances:paymentsEdit.paymentDuePlaceholder",
                                          "YYYY-MM-DD",
                                        )}
                                  </span>
                                </div>
                                <Icon
                                  className="text-muted-foreground"
                                  height="1.1rem"
                                  icon="tabler:chevron-down"
                                  width="1.1rem"
                                />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="z-[2000] w-auto p-0">
                              <Calendar
                                initialFocus
                                disabled={(date) => date <= new Date()}
                                locale={getDateFnsLocale(i18n.language)}
                                mode="single"
                                selected={selectedDate ?? undefined}
                                weekStartsOn={1}
                                onSelect={(val) => {
                                  if (!val) {
                                    field.onChange("");
                                  } else {
                                    field.onChange(format(val, "yyyy-MM-dd HH:mm"));
                                  }
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                {/* Removed extra cards; compact single-card layout above already contains fields */}

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="bg-muted/30 flex h-8 w-8 items-center justify-center rounded-lg">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="tabler:notes"
                        width="1rem"
                      />
                    </div>
                    <h3 className="text-accent-foreground text-sm font-semibold">
                      {t("finances:paymentsEdit.description", "Description")}
                    </h3>
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="sr-only">
                          {t("finances:paymentsEdit.description", "Description")}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              className="bg-muted/20 [field-sizing:content] min-h-[120px] resize-none rounded-xl border-none p-4 text-sm break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                              maxLength={2000}
                              placeholder={t(
                                "finances:paymentsEdit.descriptionPlaceholder",
                                "Optional description",
                              )}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                setDescLength(e.target.value.length);
                              }}
                            />
                            <div className="text-muted-foreground/60 absolute right-3 bottom-2 text-xs">
                              {descLength}/2000
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
                    disabled={isSubmitting}
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                  >
                    {t("common:cancel", "Cancel")}
                  </Button>
                  <Button
                    className="gap-2 rounded-xl"
                    disabled={isSubmitting || !!current?.isCompleted}
                    type="submit"
                    variant="foreground"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon
                          className="animate-spin"
                          height="1rem"
                          icon="lucide:loader-2"
                          width="1rem"
                        />
                        {t("common:saving", "Saving...")}
                      </>
                    ) : (
                      <>
                        <Icon height="1rem" icon="mdi:content-save" width="1rem" />
                        {t("common:save", "Save")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
